import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { BrandInfo, DEFAULT_CONTENT_MIX } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import { Input, InputWrapper, Label, TextArea, ErrorMessage } from '../components/Input';
import { PageSkeleton } from '../components/PageSkeleton';
import { flattenJsonForEdit } from '../utils/formatBrandData';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.medium};
  min-height: 42px;
  background: ${({ theme }) => theme.colors.surface};
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const TagRemove = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const TagInputField = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const ContentMixGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ContentMixItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ContentMixLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ContentMixLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const ContentMixHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ContentMixValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  min-width: 36px;
  text-align: right;
`;

const ContentMixSlider = styled.input`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.15s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 50%;
    border: none;
    cursor: pointer;
  }
`;

const TotalDisplay = styled.div<{ $isValid: boolean }>`
  text-align: right;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme, $isValid }) => ($isValid ? theme.colors.success : theme.colors.error)};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const SuccessMessage = styled.div`
  background: ${({ theme }) => theme.colors.success};
  color: white;
  padding: ${({ theme }) => theme.spacing.md};
  border: ${({ theme }) => theme.borders.medium};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const ColorSwatchRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ColorSwatch = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $color }) => $color};
  border: 2px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  position: relative;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.1);
  }

  &:hover::after {
    content: '\\00d7';
    position: absolute;
    top: -6px;
    right: -6px;
    background: ${({ theme }) => theme.colors.error};
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }
`;

const AddColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ColorPickerInput = styled.input`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
`;

const AddButton = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const FontTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const FontTagRemove = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 0 2px;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const HintText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

export function BrandProfilePage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState<Partial<BrandInfo>>({
    description: '',
    targetAudience: '',
    tone: '',
    keywords: [],
    businessOverview: '',
    coreIdentity: '',
    marketPositioning: '',
    competitors: [],
    customerProfile: '',
    emotionalBenefits: '',
    brandStory: '',
    brandPersonality: '',
    productsServices: [],
    serviceAreas: '',
    phoneNumber: '',
    postTopics: [],
    voiceGuide: {
      purpose: '',
      audience: '',
      tone: '',
      emotionalTone: '',
      character: '',
      syntax: '',
      language: '',
      wordsToUse: [],
      wordsToAvoid: [],
    },
    contentMix: { ...DEFAULT_CONTENT_MIX },
    brandColors: [],
    brandFonts: [],
  });

  // Tag input states
  const [keywordsInput, setKeywordsInput] = useState('');
  const [competitorsInput, setCompetitorsInput] = useState('');
  const [productsInput, setProductsInput] = useState('');
  const [topicsInput, setTopicsInput] = useState('');
  const [wordsToUseInput, setWordsToUseInput] = useState('');
  const [wordsToAvoidInput, setWordsToAvoidInput] = useState('');

  // Brand colors & fonts
  const [colorPickerValue, setColorPickerValue] = useState('#3B82F6');
  const [fontInput, setFontInput] = useState('');

  useEffect(() => {
    if (workspaceId) {
      fetchBrandInfo();
    }
  }, [workspaceId]);

  const fetchBrandInfo = async () => {
    try {
      const data = await apiClient.get<BrandInfo | null>(`/api/workspaces/${workspaceId}/brand-info`);
      if (data) {
        // Format JSON strings in text fields for better display/editing
        setFormData({
          ...data,
          // Format fields that may contain JSON strings from AI analysis
          description: flattenJsonForEdit(data.description),
          targetAudience: flattenJsonForEdit(data.targetAudience),
          tone: flattenJsonForEdit(data.tone),
          businessOverview: flattenJsonForEdit(data.businessOverview),
          coreIdentity: flattenJsonForEdit(data.coreIdentity),
          marketPositioning: flattenJsonForEdit(data.marketPositioning),
          customerProfile: flattenJsonForEdit(data.customerProfile),
          emotionalBenefits: flattenJsonForEdit(data.emotionalBenefits),
          brandStory: flattenJsonForEdit(data.brandStory),
          brandPersonality: flattenJsonForEdit(data.brandPersonality),
          contentMix: data.contentMix || { ...DEFAULT_CONTENT_MIX },
          voiceGuide: data.voiceGuide || {
            purpose: '',
            audience: '',
            tone: '',
            emotionalTone: '',
            character: '',
            syntax: '',
            language: '',
            wordsToUse: [],
            wordsToAvoid: [],
          },
        });
      }
    } catch (err) {
      console.error('Failed to fetch brand info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleVoiceGuideChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      voiceGuide: { ...prev.voiceGuide!, [field]: value },
    }));
  };

  const handleContentMixChange = (field: string, value: number) => {
    setFormData((prev) => ({
      ...prev,
      contentMix: { ...prev.contentMix!, [field]: value },
    }));
  };

  const addTag = (field: 'keywords' | 'competitors' | 'productsServices' | 'postTopics', value: string) => {
    const trimmed = value.trim();
    if (trimmed && !formData[field]?.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        [field]: [...(prev[field] || []), trimmed],
      }));
    }
  };

  const removeTag = (field: 'keywords' | 'competitors' | 'productsServices' | 'postTopics', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || [],
    }));
  };

  const addVoiceTag = (field: 'wordsToUse' | 'wordsToAvoid', value: string) => {
    const trimmed = value.trim();
    if (trimmed && !formData.voiceGuide?.[field]?.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        voiceGuide: {
          ...prev.voiceGuide!,
          [field]: [...(prev.voiceGuide?.[field] || []), trimmed],
        },
      }));
    }
  };

  const removeVoiceTag = (field: 'wordsToUse' | 'wordsToAvoid', index: number) => {
    setFormData((prev) => ({
      ...prev,
      voiceGuide: {
        ...prev.voiceGuide!,
        [field]: prev.voiceGuide?.[field]?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  const handleTagKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'keywords' | 'competitors' | 'productsServices' | 'postTopics',
    inputValue: string,
    setInputValue: (v: string) => void
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(field, inputValue);
      setInputValue('');
    }
  };

  const handleVoiceTagKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'wordsToUse' | 'wordsToAvoid',
    inputValue: string,
    setInputValue: (v: string) => void
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addVoiceTag(field, inputValue);
      setInputValue('');
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/brand-info`, formData);
      setSuccess('Brand profile saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save brand profile');
    } finally {
      setIsSaving(false);
    }
  };

  const contentMixTotal =
    (formData.contentMix?.education || 0) +
    (formData.contentMix?.connection || 0) +
    (formData.contentMix?.proof || 0) +
    (formData.contentMix?.promotion || 0);

  if (isLoading) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <div>
          <Title>Brand Profile</Title>
          <Subtitle>Edit your brand&apos;s complete profile for content generation</Subtitle>
        </div>
        <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
          Back to Workspace
        </Button>
      </PageHeader>

      {success && <SuccessMessage>{success}</SuccessMessage>}
      {error && <ErrorMessage style={{ marginBottom: 16 }}>{error}</ErrorMessage>}

      <Grid>
        {/* Brand Identity Section */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Identity</CardTitle>
              <CardDescription>Core information about your brand</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <FormGroup>
              <InputWrapper>
                <Label>Business Overview</Label>
                <TextArea
                  value={formData.businessOverview || ''}
                  onChange={(e) => handleInputChange('businessOverview', e.target.value)}
                  placeholder="e.g., We are a boutique digital marketing agency specializing in helping local restaurants and cafes grow their online presence through social media and email marketing."
                  rows={3}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Core Identity</Label>
                <TextArea
                  value={formData.coreIdentity || ''}
                  onChange={(e) => handleInputChange('coreIdentity', e.target.value)}
                  placeholder="e.g., Our mission is to make premium skincare accessible to everyone. We value transparency, sustainability, and science-backed ingredients."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Market Positioning</Label>
                <TextArea
                  value={formData.marketPositioning || ''}
                  onChange={(e) => handleInputChange('marketPositioning', e.target.value)}
                  placeholder="e.g., We position ourselves as the affordable premium option — higher quality than mass-market brands but more accessible than luxury competitors like La Mer."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Target Audience</Label>
                <TextArea
                  value={formData.targetAudience || ''}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                  placeholder="e.g., Health-conscious women aged 25-45 who care about clean ingredients and are willing to invest in their skincare routine."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Customer Profile</Label>
                <TextArea
                  value={formData.customerProfile || ''}
                  onChange={(e) => handleInputChange('customerProfile', e.target.value)}
                  placeholder="e.g., Sarah, 32, works in marketing, shops at Sephora, follows skincare influencers on Instagram, reads ingredient labels, prefers cruelty-free products."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Competitors (press Enter to add)</Label>
                <TagInput>
                  {formData.competitors?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeTag('competitors', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={competitorsInput}
                    onChange={(e) => setCompetitorsInput(e.target.value)}
                    onKeyDown={(e) => handleTagKeyDown(e, 'competitors', competitorsInput, setCompetitorsInput)}
                    placeholder="e.g., CeraVe, The Ordinary..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>
          </CardContent>
        </Card>

        {/* Brand Personality Section */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Personality</CardTitle>
              <CardDescription>Your brand&apos;s character and story</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <FormGroup>
              <InputWrapper>
                <Label>Brand Personality</Label>
                <TextArea
                  value={formData.brandPersonality || ''}
                  onChange={(e) => handleInputChange('brandPersonality', e.target.value)}
                  placeholder="e.g., Warm and approachable, like a knowledgeable friend. We're confident but never arrogant, playful but professional."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Brand Story</Label>
                <TextArea
                  value={formData.brandStory || ''}
                  onChange={(e) => handleInputChange('brandStory', e.target.value)}
                  placeholder="e.g., Founded in 2019 by a dermatologist frustrated with overpriced products filled with fillers. Started in a home lab, now sold in 500+ stores."
                  rows={3}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Emotional Benefits</Label>
                <TextArea
                  value={formData.emotionalBenefits || ''}
                  onChange={(e) => handleInputChange('emotionalBenefits', e.target.value)}
                  placeholder="e.g., Customers feel empowered and confident knowing they're using clean, effective products. They feel smart for choosing quality over hype."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Products/Services (press Enter to add)</Label>
                <TagInput>
                  {formData.productsServices?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeTag('productsServices', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={productsInput}
                    onChange={(e) => setProductsInput(e.target.value)}
                    onKeyDown={(e) => handleTagKeyDown(e, 'productsServices', productsInput, setProductsInput)}
                    placeholder="e.g., Facial Serum, Moisturizer..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Service Areas</Label>
                <Input
                  value={formData.serviceAreas || ''}
                  onChange={(e) => handleInputChange('serviceAreas', e.target.value)}
                  placeholder="e.g., United States, Canada, UK — ships worldwide"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phoneNumber || ''}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Contact phone number..."
                />
              </InputWrapper>
            </FormGroup>
          </CardContent>
        </Card>

        {/* Brand Voice Section */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Voice Guide</CardTitle>
              <CardDescription>How your brand communicates</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <FormGroup>
              <InputWrapper>
                <Label>Voice Purpose</Label>
                <TextArea
                  value={formData.voiceGuide?.purpose || ''}
                  onChange={(e) => handleVoiceGuideChange('purpose', e.target.value)}
                  placeholder="e.g., To educate our audience about skincare science in an approachable way, build trust, and position us as the go-to resource for clean beauty."
                  rows={2}
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Voice Tone</Label>
                <Input
                  value={formData.voiceGuide?.tone || ''}
                  onChange={(e) => handleVoiceGuideChange('tone', e.target.value)}
                  placeholder="e.g., Professional yet warm, Conversational, Witty"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Emotional Tone</Label>
                <Input
                  value={formData.voiceGuide?.emotionalTone || ''}
                  onChange={(e) => handleVoiceGuideChange('emotionalTone', e.target.value)}
                  placeholder="e.g., Empowering, Reassuring, Inspiring confidence"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Voice Character</Label>
                <Input
                  value={formData.voiceGuide?.character || ''}
                  onChange={(e) => handleVoiceGuideChange('character', e.target.value)}
                  placeholder="e.g., Knowledgeable friend, Trusted advisor, Coach"
                />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Words to Use (press Enter to add)</Label>
                <TagInput>
                  {formData.voiceGuide?.wordsToUse?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeVoiceTag('wordsToUse', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={wordsToUseInput}
                    onChange={(e) => setWordsToUseInput(e.target.value)}
                    onKeyDown={(e) => handleVoiceTagKeyDown(e, 'wordsToUse', wordsToUseInput, setWordsToUseInput)}
                    placeholder="e.g., Glow, Nourish, Radiant..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Words to Avoid (press Enter to add)</Label>
                <TagInput>
                  {formData.voiceGuide?.wordsToAvoid?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeVoiceTag('wordsToAvoid', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={wordsToAvoidInput}
                    onChange={(e) => setWordsToAvoidInput(e.target.value)}
                    onKeyDown={(e) => handleVoiceTagKeyDown(e, 'wordsToAvoid', wordsToAvoidInput, setWordsToAvoidInput)}
                    placeholder="e.g., Cheap, Miracle, Anti-aging..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>
          </CardContent>
        </Card>

        {/* Content Strategy Section */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Content Strategy</CardTitle>
              <CardDescription>Topics and content mix for posts</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <FormGroup>
              <InputWrapper>
                <Label>Post Topics (press Enter to add)</Label>
                <TagInput>
                  {formData.postTopics?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeTag('postTopics', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={topicsInput}
                    onChange={(e) => setTopicsInput(e.target.value)}
                    onKeyDown={(e) => handleTagKeyDown(e, 'postTopics', topicsInput, setTopicsInput)}
                    placeholder="Add topic (e.g., Mold Remediation, Air Quality)..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Keywords (press Enter to add)</Label>
                <TagInput>
                  {formData.keywords?.map((item, i) => (
                    <Tag key={i}>
                      {item}
                      <TagRemove onClick={() => removeTag('keywords', i)}>×</TagRemove>
                    </Tag>
                  ))}
                  <TagInputField
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    onKeyDown={(e) => handleTagKeyDown(e, 'keywords', keywordsInput, setKeywordsInput)}
                    placeholder="e.g., clean beauty, skincare..."
                  />
                </TagInput>
              </InputWrapper>
            </FormGroup>

            <Section>
              <SectionTitle>Content Mix (%)</SectionTitle>
              <ContentMixGrid>
                <ContentMixItem>
                  <ContentMixLabelRow>
                    <div>
                      <ContentMixLabel>Value & Education</ContentMixLabel>
                      <ContentMixHint> — Tips, how-tos, industry insights</ContentMixHint>
                    </div>
                    <ContentMixValue>{formData.contentMix?.education || 0}%</ContentMixValue>
                  </ContentMixLabelRow>
                  <ContentMixSlider
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.contentMix?.education || 0}
                    onChange={(e) => handleContentMixChange('education', parseInt(e.target.value) || 0)}
                  />
                </ContentMixItem>
                <ContentMixItem>
                  <ContentMixLabelRow>
                    <div>
                      <ContentMixLabel>Connection & Story</ContentMixLabel>
                      <ContentMixHint> — Behind-the-scenes, community, engagement</ContentMixHint>
                    </div>
                    <ContentMixValue>{formData.contentMix?.connection || 0}%</ContentMixValue>
                  </ContentMixLabelRow>
                  <ContentMixSlider
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.contentMix?.connection || 0}
                    onChange={(e) => handleContentMixChange('connection', parseInt(e.target.value) || 0)}
                  />
                </ContentMixItem>
                <ContentMixItem>
                  <ContentMixLabelRow>
                    <div>
                      <ContentMixLabel>Proof & Authority</ContentMixLabel>
                      <ContentMixHint> — Testimonials, case studies, results</ContentMixHint>
                    </div>
                    <ContentMixValue>{formData.contentMix?.proof || 0}%</ContentMixValue>
                  </ContentMixLabelRow>
                  <ContentMixSlider
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.contentMix?.proof || 0}
                    onChange={(e) => handleContentMixChange('proof', parseInt(e.target.value) || 0)}
                  />
                </ContentMixItem>
                <ContentMixItem>
                  <ContentMixLabelRow>
                    <div>
                      <ContentMixLabel>Direct Promotion</ContentMixLabel>
                      <ContentMixHint> — Sales, offers, CTAs, product highlights</ContentMixHint>
                    </div>
                    <ContentMixValue>{formData.contentMix?.promotion || 0}%</ContentMixValue>
                  </ContentMixLabelRow>
                  <ContentMixSlider
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.contentMix?.promotion || 0}
                    onChange={(e) => handleContentMixChange('promotion', parseInt(e.target.value) || 0)}
                  />
                </ContentMixItem>
              </ContentMixGrid>
              <TotalDisplay $isValid={contentMixTotal === 100}>
                Total: {contentMixTotal}% {contentMixTotal !== 100 && '(should be 100%)'}
              </TotalDisplay>
            </Section>
          </CardContent>
        </Card>

        {/* Brand Visual Identity Section */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Visual Identity</CardTitle>
              <CardDescription>Colors and fonts that represent your brand</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <FormGroup>
              <InputWrapper>
                <Label>Brand Colors (max 6, click to remove)</Label>
                <ColorSwatchRow>
                  {(formData.brandColors || []).map((color, i) => (
                    <ColorSwatch
                      key={i}
                      $color={color}
                      title={`${color} — click to remove`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          brandColors: (prev.brandColors || []).filter((_, idx) => idx !== i),
                        }))
                      }
                    />
                  ))}
                  {(formData.brandColors || []).length < 6 && (
                    <AddColorRow>
                      <ColorPickerInput
                        type="color"
                        value={colorPickerValue}
                        onChange={(e) => setColorPickerValue(e.target.value)}
                      />
                      <AddButton
                        type="button"
                        onClick={() => {
                          if (colorPickerValue && !(formData.brandColors || []).includes(colorPickerValue)) {
                            setFormData((prev) => ({
                              ...prev,
                              brandColors: [...(prev.brandColors || []), colorPickerValue],
                            }));
                          }
                        }}
                      >
                        + Add
                      </AddButton>
                    </AddColorRow>
                  )}
                </ColorSwatchRow>
                {(formData.brandColors || []).length === 0 && (
                  <HintText>No brand colors set. Add colors to use in templates with Brand color mode.</HintText>
                )}
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <Label>Brand Fonts (max 3)</Label>
                <ColorSwatchRow>
                  {(formData.brandFonts || []).map((font, i) => (
                    <FontTag key={i}>
                      {font}
                      <FontTagRemove
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            brandFonts: (prev.brandFonts || []).filter((_, idx) => idx !== i),
                          }))
                        }
                      >
                        ×
                      </FontTagRemove>
                    </FontTag>
                  ))}
                </ColorSwatchRow>
                {(formData.brandFonts || []).length < 3 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                    <Input
                      value={fontInput}
                      onChange={(e) => setFontInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const trimmed = fontInput.trim();
                          if (trimmed && !(formData.brandFonts || []).includes(trimmed)) {
                            setFormData((prev) => ({
                              ...prev,
                              brandFonts: [...(prev.brandFonts || []), trimmed],
                            }));
                            setFontInput('');
                          }
                        }
                      }}
                      placeholder="e.g., Poppins, Montserrat..."
                      style={{ flex: 1 }}
                    />
                    <AddButton
                      type="button"
                      onClick={() => {
                        const trimmed = fontInput.trim();
                        if (trimmed && !(formData.brandFonts || []).includes(trimmed)) {
                          setFormData((prev) => ({
                            ...prev,
                            brandFonts: [...(prev.brandFonts || []), trimmed],
                          }));
                          setFontInput('');
                        }
                      }}
                    >
                      + Add
                    </AddButton>
                  </div>
                )}
                {(formData.brandFonts || []).length === 0 && (
                  <HintText>No brand fonts set. Add font names used in your brand guidelines.</HintText>
                )}
              </InputWrapper>
            </FormGroup>
          </CardContent>
        </Card>
      </Grid>

      <ButtonGroup>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Brand Profile'}
        </Button>
        <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}`)}>
          Cancel
        </Button>
      </ButtonGroup>
    </>
  );
}
